/**
 * Mixin compartilhado por todos os filtros filhos (Select, Input, ...).
 *
 * Responsabilidades:
 *  - se registrar no <FilterBuilder> pai (provide/inject)
 *  - manter o rascunho (draft) enquanto o usuário edita
 *  - só emitir `input` (v-model) quando o pai chamar apply()/clear()
 *  - com <FilterBuilder auto-apply>, emitir `input` a cada mudança do rascunho
 *
 *  - `disabled`: bloqueia a interação com o filtro (linha e item do menu)
 *  - `tooltip` + `tooltip-text` (ou slot #tooltip): texto de ajuda opcional
 *
 * Cada filtro novo só precisa sobrescrever `getEmptyValue()` (se necessário)
 * e renderizar o próprio controle dentro de <FilterBuilderRow v-bind="rowProps()">,
 * repassando o slot #tooltip e aplicando :disabled="disabled" no controle.
 */
const clone = (v) => (Array.isArray(v) ? [...v] : v);

export default {
    inject: {
        filterBuilder: { default: null },
    },
    props: {
        value: { default: null },
        title: { type: String, required: true },
        icon: { type: String, default: 'el-icon-s-operation' },
        // Impede a interação do usuário com este filtro
        disabled: { type: Boolean, default: false },
        // Liga/desliga o tooltip de ajuda (só aparece se houver texto ou slot #tooltip)
        tooltip: { type: Boolean, default: false },
        tooltipText: { type: String, default: '' },
        tooltipPlacement: { type: String, default: 'top' },
    },
    data() {
        return {
            active: false,   // a condição está visível na lista?
            order: 0,        // ordem de ativação (para listar na ordem em que foram adicionadas)
            draft: this.getEmptyValue(),
        };
    },
    computed: {
        index() {
            return this.filterBuilder ? this.filterBuilder.activeItems.indexOf(this) : 0;
        },
        hasValue() {
            return !this.isEmpty(this.value);
        },
    },
    watch: {
        // Mudou de fora (ex.: reset externo do filtro) -> sincroniza
        value(v) {
            // Eco do próprio auto-apply: não re-sincroniza (evita loop e não esconde a linha)
            if (this.pendingAutoValue !== undefined && this.isSame(v, this.pendingAutoValue)) {
                this.pendingAutoValue = undefined;
                return;
            }
            this.pendingAutoValue = undefined;
            this.syncFromValue(v);
        },
        // Modo auto-apply: cada alteração do rascunho já vira v-model
        draft: {
            deep: true,
            handler(v) {
                // Permite ao pai reagir ao rascunho antes do "Aplicar" (ex.: UF -> Cidade)
                this.$emit('change', v);

                if (!this.filterBuilder || !this.filterBuilder.autoApply) return;
                if (this.isSame(v, this.value)) return;

                const out = this.isEmpty(v) ? this.getEmptyValue() : clone(v);
                this.pendingAutoValue = out;
                this.$emit('input', out);
                this.filterBuilder.notifyAutoApply();
            },
        },
    },
    created() {
        this.pendingAutoValue = undefined; // não reativo de propósito
        if (this.filterBuilder) this.filterBuilder.register(this);
        this.syncFromValue(this.value);
    },
    beforeDestroy() {
        if (this.filterBuilder) this.filterBuilder.unregister(this);
    },
    methods: {
        /** Tooltip habilitado E com conteúdo (prop ou slot). Método pois $scopedSlots não é reativo. */
        hasTooltip() {
            if (!this.tooltip) return false;
            return !!(this.$scopedSlots.tooltip || (this.tooltipText && this.tooltipText.trim()));
        },
        /** Conteúdo do tooltip (slot tem prioridade sobre a prop) */
        tooltipContent() {
            return this.$scopedSlots.tooltip ? this.$scopedSlots.tooltip() : this.tooltipText;
        },
        /** Props repassadas ao <FilterBuilderRow> */
        rowProps() {
            return {
                index: this.index,
                title: this.title,
                icon: this.icon,
                disabled: this.disabled,
                tooltip: this.hasTooltip(),
                tooltipText: this.tooltipText,
                tooltipPlacement: this.tooltipPlacement,
            };
        },
        getEmptyValue() {
            return null;
        },
        isEmpty(v) {
            return v === null || v === undefined || v === '' || (Array.isArray(v) && v.length === 0);
        },
        // Igualdade rasa (arrays por item); dois vazios quaisquer são iguais
        isSame(a, b) {
            if (this.isEmpty(a) && this.isEmpty(b)) return true;
            if (Array.isArray(a) && Array.isArray(b)) {
                return a.length === b.length && a.every((x, i) => x === b[i]);
            }
            return a === b;
        },
        syncFromValue(v) {
            const empty = this.isEmpty(v);
            this.draft = empty ? this.getEmptyValue() : clone(v);
            this.active = !empty;
            if (this.active && !this.order && this.filterBuilder) {
                this.order = this.filterBuilder.nextOrder();
            }
        },
        activate() {
            this.active = true;
            this.order = this.filterBuilder ? this.filterBuilder.nextOrder() : 0;
        },
        remove() {
            this.active = false;
            this.draft = this.getEmptyValue();
        },
        /** Chamado pelo pai em "Aplicar filtro" */
        apply() {
            const empty = this.isEmpty(this.draft);
            if (empty) this.remove();
            this.$emit('input', empty ? this.getEmptyValue() : clone(this.draft));
        },
        /** Chamado pelo pai em "Limpar tudo" */
        clear() {
            this.remove();
            this.$emit('input', this.getEmptyValue());
        },
    },
};


// WEBPACK FOOTER //
// ./resources/assets/js/components/_micro/FilterBuilder/filterBuilderItem.js